import { useLocalization } from "@tm/localization"
import { useSelector } from "react-redux"
import { useTyresV2 } from "@tm/context-distribution"
import { TyreFilter } from "@tm/models"
import { AccordionStates, FiltersOrder, FiltersOrderByCartype, IListFilters } from "../business"
import { MainState } from "../../main"
import { AvailabilityFilter, FiltersArea } from "./FiltersArea"
import { SeasonComponent } from "./SeasonComponent"
import { FilterItem } from "./FilterItem"
import { EuTyreLabel } from "./EuTyreLabel"

type Props = {
    cartype: FiltersOrder
    accordionsState: AccordionStates
    handleFilterReset: (path?: IListFilters) => void
    updateAccordionState: (key: string, value: boolean) => void
    handleFilterChange: (filter: TyreFilter | AvailabilityFilter | null, path?: IListFilters) => void
    handleEuFiltersReset: () => void
}

export function TyresFiltersByCartype(props: Props) {
    const { cartype, accordionsState, handleFilterReset, handleFilterChange, updateAccordionState, handleEuFiltersReset } = props
    const { translateText } = useLocalization()
    const filters = useSelector((s: MainState) => s.list.filters)
    const selectedFilters = useSelector((s: MainState) => s.list.selectedFilters)
    const hasTyresV2 = useTyresV2()

    const showApplicationArea = hasTyresV2 && (!!filters.application?.length || !!filters.axlePosition?.length)
    const showFeatures =
        hasTyresV2 && (!!filters.ev?.length || !!filters.construction?.length || !!filters.seal?.length || !!filters.threePMSF?.length)

    function renderGeneralFilter(
        path: IListFilters | undefined,
        headline: string,
        data: TyreFilter[] | undefined,
        currentFilters: TyreFilter[] | undefined,
        isExpanded: boolean | undefined,
        leftLoading?: boolean | undefined
    ) {
        return (
            <FiltersArea
                path={path}
                data={data}
                headline={headline}
                onChange={handleFilterChange}
                selectedFilters={currentFilters}
                onResetFilters={handleFilterReset}
                isExpanded={isExpanded}
                onAccordionClick={updateAccordionState}
                disabled={leftLoading}
            />
        )
    }

    function renderDestructuredFilter(
        path: IListFilters | undefined,
        data: TyreFilter[] | undefined,
        currentFilters: TyreFilter[] | undefined,
        leftLoading?: boolean | undefined
    ) {
        return data?.length
            ? data?.map((filter) => (
                  <FilterItem
                      key={filter.value}
                      filter={filter}
                      checked={!!currentFilters?.length}
                      onChange={() => handleFilterChange(filter, path)}
                      disabled={leftLoading}
                  />
              ))
            : null
    }

    function renderSeason() {
        return filters.season?.length ? (
            <FiltersArea
                path="season"
                headline={translateText(1235)}
                onResetFilters={handleFilterReset}
                hideFilterSearch
                isExpanded={!!accordionsState.season}
                onAccordionClick={updateAccordionState}
            >
                <SeasonComponent
                    handleFilterChange={handleFilterChange}
                    seasons={filters.season}
                    selectedSeason={selectedFilters.season}
                    disabled={filters.leftLoading}
                />
            </FiltersArea>
        ) : null
    }

    function renderApplicationArea() {
        return showApplicationArea ? (
            <FiltersArea
                headline={translateText(13962)}
                isExpanded={!!accordionsState.applicationArea}
                onAccordionClick={updateAccordionState}
                dropdownPath="applicationArea"
                hideFilterSearch
                hideActions
                disabled={filters.leftLoading}
            >
                {!!filters.application?.length &&
                    renderGeneralFilter(
                        "application",
                        translateText(13963),
                        filters.application,
                        selectedFilters.application,
                        !!accordionsState.application,
                        filters.leftLoading
                    )}
                {!!filters.axlePosition?.length &&
                    renderGeneralFilter(
                        "axlePosition",
                        translateText(13970),
                        filters.axlePosition,
                        selectedFilters.axlePosition,
                        !!accordionsState.axlePosition,
                        filters.leftLoading
                    )}
            </FiltersArea>
        ) : null
    }

    function renderWetGripGlass() {
        return !!filters.wetGripClass?.length || !!filters.fuelEfficiency?.length || !!filters.externalRolling?.length ? (
            <FiltersArea
                path="wetGripClass"
                headline={translateText(12404)}
                onChange={handleFilterChange}
                onResetFilters={handleFilterReset}
                onResetEuFilters={handleEuFiltersReset}
                hideFilterSearch
                isExpanded={!!accordionsState.wetGripClass}
                onAccordionClick={updateAccordionState}
                disabled={filters.leftLoading}
            >
                <EuTyreLabel onChange={handleFilterChange} onResetFilters={handleEuFiltersReset} disabled={filters.leftLoading} />
            </FiltersArea>
        ) : null
    }

    return (
        <>
            {FiltersOrderByCartype[cartype]
                .map((item) => {
                    if (typeof item === "string") {
                        switch (item) {
                            case "season":
                                return renderSeason()
                            case "applicationArea":
                                return renderApplicationArea()
                            case "manufacturer":
                                return filters.manufacturer?.length
                                    ? renderGeneralFilter(
                                          item,
                                          translateText(71),
                                          filters.manufacturer,
                                          selectedFilters.manufacturer,
                                          !!accordionsState.manufacturer
                                      )
                                    : null
                            case "loadIndex":
                                return filters.loadIndex?.length
                                    ? renderGeneralFilter(
                                          item,
                                          translateText(12403),
                                          filters.loadIndex,
                                          selectedFilters.loadIndex,
                                          !!accordionsState.loadIndex,
                                          filters.leftLoading
                                      )
                                    : null
                            case "speedIndex":
                                return filters.speedIndex?.length
                                    ? renderGeneralFilter(
                                          item,
                                          translateText(719),
                                          filters.speedIndex,
                                          selectedFilters.speedIndex,
                                          !!accordionsState.speedIndex,
                                          filters.leftLoading
                                      )
                                    : null
                            case "oeIdentifier":
                                return filters.oeIdentifier?.length
                                    ? renderGeneralFilter(
                                          item,
                                          translateText(720),
                                          filters.oeIdentifier,
                                          selectedFilters.oeIdentifier,
                                          !!accordionsState.oeIdentifier,
                                          filters.leftLoading
                                      )
                                    : null
                            case "plyRating":
                                return hasTyresV2 && !!filters.plyRating?.length
                                    ? renderGeneralFilter(
                                          item,
                                          translateText(14026),
                                          filters.plyRating,
                                          selectedFilters.plyRating,
                                          !!accordionsState.plyRating,
                                          filters.leftLoading
                                      )
                                    : null
                            case "tyreTread":
                                return hasTyresV2 && !!filters.tyreTread?.length
                                    ? renderGeneralFilter(
                                          item,
                                          translateText(13077),
                                          filters.tyreTread,
                                          selectedFilters.tyreTread,
                                          !!accordionsState.tyreTread,
                                          filters.leftLoading
                                      )
                                    : null
                            case "hardness":
                                return hasTyresV2 && !!filters.hardness?.length
                                    ? renderGeneralFilter(
                                          item,
                                          translateText(13994),
                                          filters.hardness,
                                          selectedFilters.hardness,
                                          !!accordionsState.hardness,
                                          filters.leftLoading
                                      )
                                    : null
                            case "tubeType":
                                return hasTyresV2 && !!filters.tubeType?.length
                                    ? renderGeneralFilter(
                                          item,
                                          translateText(13998),
                                          filters.tubeType,
                                          selectedFilters.tubeType,
                                          !!accordionsState.tubeType,
                                          filters.leftLoading
                                      )
                                    : null
                            case "construction":
                                return hasTyresV2 && !!filters.construction?.length
                                    ? renderGeneralFilter(
                                          item,
                                          translateText(14028),
                                          filters.construction,
                                          selectedFilters.construction,
                                          !!accordionsState.construction,
                                          filters.leftLoading
                                      )
                                    : null
                            case "sidewall":
                                return hasTyresV2 && !!filters.sidewall?.length
                                    ? renderGeneralFilter(
                                          item,
                                          translateText(13983),
                                          filters.sidewall,
                                          selectedFilters.sidewall,
                                          !!accordionsState.sidewall,
                                          filters.leftLoading
                                      )
                                    : null
                            case "wetGripClass":
                                return renderWetGripGlass()
                            default:
                                return null
                        }
                    }

                    if (typeof item === "object") {
                        return Object.entries(item).map(([groupKey, subKeys]) => {
                            if (groupKey === "features") {
                                return showFeatures ? (
                                    <FiltersArea
                                        headline={translateText(397)}
                                        isExpanded={!!accordionsState.features}
                                        onAccordionClick={updateAccordionState}
                                        dropdownPath="features"
                                        onResetFilters={handleFilterReset}
                                        hideFilterSearch
                                        hideActions
                                        disabled={filters.leftLoading}
                                    >
                                        {(subKeys as string[]).map((subKey) => {
                                            switch (subKey) {
                                                case "seal":
                                                    return filters.seal?.length
                                                        ? renderGeneralFilter(
                                                              subKey,
                                                              translateText(14044),
                                                              filters.seal,
                                                              selectedFilters.seal,
                                                              !!accordionsState.seal,
                                                              filters.leftLoading
                                                          )
                                                        : null
                                                case "ev":
                                                    return renderDestructuredFilter(subKey, filters.ev, selectedFilters.ev, filters.leftLoading)
                                                case "threePMSF":
                                                    return renderDestructuredFilter(
                                                        subKey,
                                                        filters.threePMSF,
                                                        selectedFilters.threePMSF,
                                                        filters.leftLoading
                                                    )
                                                case "construction":
                                                    return renderDestructuredFilter(
                                                        subKey,
                                                        filters.construction,
                                                        selectedFilters.construction,
                                                        filters.leftLoading
                                                    )
                                                default:
                                                    return null
                                            }
                                        })}
                                    </FiltersArea>
                                ) : null
                            }

                            if (groupKey === "extras") {
                                return filters.extras?.length ? (
                                    <FiltersArea
                                        path="extras"
                                        data={filters.extras}
                                        headline={translateText(744)}
                                        onChange={handleFilterChange}
                                        selectedFilters={selectedFilters.extras}
                                        onResetFilters={handleFilterReset}
                                        hasInlineInfo
                                        isExpanded={!!accordionsState.extras}
                                        onAccordionClick={updateAccordionState}
                                        disabled={filters.leftLoading}
                                    >
                                        {(subKeys as string[]).map((subKey) => {
                                            if (subKey === "sidewall") {
                                                return hasTyresV2 && !!filters.sidewall?.length
                                                    ? renderGeneralFilter(
                                                          subKey,
                                                          translateText(13983),
                                                          filters.sidewall,
                                                          selectedFilters.sidewall,
                                                          !!accordionsState.sidewall,
                                                          filters.leftLoading
                                                      )
                                                    : null
                                            }
                                            return null
                                        })}
                                    </FiltersArea>
                                ) : null
                            }

                            return null
                        })
                    }

                    return null
                })
                .flat()}
        </>
    )
}
